import {
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { UpdateProduct } from "../../../../../api/inventory";
import { CheckRounded, CloseRounded } from "@mui/icons-material";
import { InventoryData } from "../../../../../types/inventoryData";
import EnhancedTableHead from "./EnhancedTableHead";
import { useInventory } from "../../../InventoryProvider";
import QrScannerFind from "../../popups/QrScannerFind";
import { getProductStatusColor } from "../../../../../helpers/utils";
import { productStatusOptions } from "../../../../../helpers/selectOptions";
import { ErrorCodes } from "../../../../../types/enums";
import { useApplicationContext } from "../../../../ContextProviders/ApplicationContext";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { inventoryRules } from "../../../../../helpers/validationSchemes";
import { useTranslation } from "react-i18next";

const InventoryTable: FC<{ batchEditMode: boolean }> = ({ batchEditMode }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [qrScannerToggle, setQrScannerToggle] = useState<boolean>(false);

  const {
    rows,
    filteredRows,
    setFilteredRows,
    editingRow,
    setEditingRow,
    selectedFilterValues,
    setSelectedFilterValues,
    addBatchNumberAndActions,
    updateSingleRow,
    headCells,
    plans,
    fetchPlans,
  } = useInventory();
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();

  const handleKeyPress = (
    row: InventoryData,
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (e.key === "Enter") {
      handleSave(row);
    }
  };

  const handleSave: SubmitHandler<InventoryData> = async (
    row: InventoryData
  ) => {
    if (!isDirty) {
      setEditingRow(null);
      reset(rows[0]);
      return;
    }

    if (row.plan?.inventoryPlanId == null) row.plan = null;

    try {
      const updatedProduct: InventoryData = await UpdateProduct(row);
      const newUpdatedProduct = addBatchNumberAndActions(updatedProduct);

      updateSingleRow(newUpdatedProduct);
      fetchPlans();

      setEditingRow(null);
      showSnackbar(
        `${updatedProduct.serial} ${t("inventory.successupdate")}`,
        "success"
      );
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case ErrorCodes.ProductNotFound:
            showSnackbar(t("errors.ProductNotFound"), "error");
            break;
          default:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
        }
      } else {
        showSnackbar(t("errors.UnexpectedError"), "error");
      }
    }
  };

  const handleCancel = async (row: InventoryData) => {
    setEditingRow(null);
    reset();
  };

  const handleSelectAllCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked && rows) {
      setFilteredRows((prev) => {
        const newFilteredRows = prev.map((oldrow) => {
          return {
            ...oldrow,
            isSelected: true,
          };
        });
        return newFilteredRows;
      });
    } else {
      setFilteredRows((prev) => {
        const newFilteredRows = prev.map((oldrow) => {
          return {
            ...oldrow,
            isSelected: false,
          };
        });
        return newFilteredRows;
      });
    }
  };

  const handleCheckboxClick = (id: number) => {
    setFilteredRows((prev) => {
      const newFilteredRows = prev.map((oldrow) => {
        if (oldrow.productId == id) {
          return {
            ...oldrow,
            isSelected: !oldrow.isSelected,
          };
        } else return oldrow;
      });
      return newFilteredRows;
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setFilteredRows(
      rows.filter((row) =>
        (
          Object.entries(selectedFilterValues) as [
            keyof InventoryData,
            (string | number)[]
          ][]
        ).every(([key, values]) => {
          if (values.length === 0) return true;

          const headCell = headCells.find((i) => i.id == key);
          if (!headCell) {
            return;
          }

          const rowValue = headCell.getValue(row);

          return typeof rowValue === "string" || typeof rowValue === "number"
            ? values.includes(rowValue)
            : false;
        })
      )
    );

    // This was the previous code. AI suggested the code above.
    // setFilteredRows(
    //     rows.filter((row) => {
    //       return (
    //         (selectedFilterValues.batchnumber.length === 0 ||
    //           selectedFilterValues.batchnumber.includes(row.batchnumber)) &&
    //         (selectedFilterValues.location.length === 0 ||
    //           selectedFilterValues.location.includes(row.location ?? "")) &&
    //         (selectedFilterValues.note.length === 0 ||
    //           selectedFilterValues.note.includes(row.note ?? "")) &&
    //         (selectedFilterValues.productType.length === 0 ||
    //           selectedFilterValues.productType.includes(row.productType)) &&
    //         (selectedFilterValues.serial.length === 0 ||
    //           selectedFilterValues.serial.includes(row.serial)) &&
    //         (selectedFilterValues.side.length === 0 ||
    //           selectedFilterValues.side.includes(row.side)) &&
    //         (selectedFilterValues.size.length === 0 ||
    //           selectedFilterValues.size.includes(row.size)) &&
    //         (selectedFilterValues.status.length === 0 ||
    //           selectedFilterValues.status.includes(row.status))
    //       );
    //     })
    //   );
  }, [selectedFilterValues, rows]);

  const handleFilterChange = (
    headCell: string,
    newValues: (string | number)[]
  ) => {
    setSelectedFilterValues((prev) => ({
      ...prev,
      [headCell]: newValues,
    }));
  };

  const [visibleRows, setVisibleRows] = useState<InventoryData[]>();

  useEffect(() => {
    setVisibleRows(
      filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    );
  }, [filteredRows, page, rowsPerPage]);

  interface AdvTableCellTypes extends TableCellProps {
    type: keyof InventoryData;
    row: InventoryData;
    children?: any;
  }

  useEffect(() => {
    if (editingRow) {
      reset(editingRow);
    }
  }, [editingRow]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<InventoryData>({
    defaultValues: editingRow || rows[0],
    mode: "onChange",
  });

  const AdvTableCell: FC<AdvTableCellTypes> = ({
    type,
    row,
    children,
    ...rest
  }) => {
    const foundHeadcell = headCells.find((h) => h.id === type);
    const value = foundHeadcell?.formatValue
      ? foundHeadcell.formatValue(row)
      : foundHeadcell?.getValue(row);
    return (
      foundHeadcell?.visible && (
        <TableCell {...rest}>{children ? children : value}</TableCell>
      )
    );
  };

  return (
    <>
      {qrScannerToggle && (
        <QrScannerFind
          onScannedListChange={(_, QrValueJustScanned) => {
            if (selectedFilterValues.serial.includes(QrValueJustScanned))
              return;
            handleFilterChange("serial", [
              ...selectedFilterValues.serial,
              QrValueJustScanned,
            ]);
          }}
          qrScannerToggle={qrScannerToggle}
          setQrScannerToggleFalse={() => setQrScannerToggle(false)}
        />
      )}
      <TableContainer
        sx={
          {
            // overflowY: "auto",
            // overflowX: "hidden",
          }
        }
      >
        <Table stickyHeader>
          <EnhancedTableHead
            handleSelectAllCheckbox={handleSelectAllCheckbox}
            handleFilterChange={handleFilterChange}
            selectedValues={selectedFilterValues}
            setQrScannerToggle={setQrScannerToggle}
            batchEditMode={batchEditMode}
          />
          <TableBody>
            {visibleRows &&
              visibleRows.map((row) => {
                return (
                  <TableRow key={row.productId} selected={row.isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        // This is stupid but necessary. 11/09/2025 ⬇️
                        checked={row.isSelected == true ? true : false}
                        onClick={() => handleCheckboxClick(row.productId)}
                      />
                    </TableCell>

                    <AdvTableCell type="productType" row={row} />
                    <AdvTableCell type="size" row={row} />
                    <AdvTableCell type="side" row={row} />
                    {editingRow?.productId === row.productId ? (
                      <TableCell>
                        <Controller
                          control={control}
                          name="status"
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              select
                              {...field}
                              size="small"
                              id="select-status"
                            >
                              {productStatusOptions.map((i) => (
                                <MenuItem key={i.value} value={i.value}>
                                  {i.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </TableCell>
                    ) : (
                      <AdvTableCell
                        type="status"
                        row={row}
                        sx={{ color: getProductStatusColor(row) }}
                      />
                    )}
                    {editingRow?.productId === row.productId ? (
                      <TableCell>
                        <Controller
                          control={control}
                          name="location"
                          rules={inventoryRules.locationRule}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              required
                              hiddenLabel
                              variant="filled"
                              size="small"
                              error={!!error}
                              helperText={
                                <>
                                  {/* Counter */}
                                  {`${field?.value?.length || 0}/${
                                    inventoryRules.locationRuleMaxLength
                                  }`}

                                  {/* Error */}
                                  {error?.message &&
                                    `. ${t(error.message).replace(
                                      "{0}",
                                      inventoryRules.locationRuleMaxLength.toString()
                                    )}`}
                                </>
                              }
                              onKeyDown={(e) => handleKeyPress(row, e)}
                            />
                          )}
                        />
                      </TableCell>
                    ) : (
                      <AdvTableCell type="location" row={row} />
                    )}
                    {editingRow?.productId === row.productId ? (
                      <TableCell>
                        <Controller
                          control={control}
                          name="note"
                          rules={inventoryRules.noteRule}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              hiddenLabel
                              variant="filled"
                              size="small"
                              value={field.value || ""}
                              onKeyDown={(e) => handleKeyPress(row, e)}
                              error={!!error}
                              helperText={
                                <>
                                  {/* Counter */}
                                  {`${field?.value?.length || 0}/${
                                    inventoryRules.noteRuleMaxLength
                                  }`}

                                  {/* Error */}
                                  {error?.message &&
                                    `. ${t(error.message).replace(
                                      "{0}",
                                      inventoryRules.noteRuleMaxLength.toString()
                                    )}`}
                                </>
                              }
                            />
                          )}
                        />
                      </TableCell>
                    ) : (
                      <AdvTableCell type="note" row={row} />
                    )}
                    {editingRow?.productId === row.productId ? (
                      <TableCell>
                        <Controller
                          control={control}
                          name="plan.inventoryPlanId"
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              select
                              {...field}
                              size="small"
                              id="select-plan"
                              value={field.value ?? ""}
                              onChange={(e) => {
                                const val =
                                  e.target.value == "" ? null : e.target.value;
                                field.onChange(val);
                              }}
                            >
                              <MenuItem key={""} value={""}>
                                {t("labels.none")}
                              </MenuItem>
                              {plans.map((p) => (
                                <MenuItem
                                  key={p.inventoryPlanId}
                                  value={p.inventoryPlanId}
                                >
                                  {p.companyName}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </TableCell>
                    ) : (
                      <AdvTableCell type="plan" row={row} />
                    )}
                    <AdvTableCell type="serial" row={row} />
                    <AdvTableCell type="batchnumber" row={row} />

                    {editingRow?.productId === row.productId ? (
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <IconButton onClick={handleSubmit(handleSave)}>
                            <CheckRounded />
                          </IconButton>
                          <IconButton onClick={() => handleCancel(row)}>
                            <CloseRounded />
                          </IconButton>
                        </Box>
                      </TableCell>
                    ) : (
                      <AdvTableCell type="actions" row={row} padding="none">
                        {row.actions}
                      </AdvTableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[2, 5, 10, 20, 40, 60, 80, 100, 150, 200]}
        component="div"
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("inventory.rowsPerPage")}
      />
    </>
  );
};

export default InventoryTable;
